import React, { useState } from 'react';
import { ScrollText } from 'lucide-react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { Spinner } from './codeBlockUtils';

import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the human-readable slug from a composite DynamoDB ID.
 * "user#userId#my-skill" → "my-skill"
 * "template#uuid#perm-test-template" → "perm-test-template"
 * "my-skill" → "my-skill" (already clean)
 */
function extractSlug(id: string | null): string | null {
  if (!id) return null;
  const parts = id.split('#');
  return parts.length >= 3 ? parts[parts.length - 1] : id;
}

function scopeStyle(scope?: string) {
  switch (scope) {
    case 'user':
      return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    case 'tenant':
      return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
    case 'template':
      return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    case 'built-in':
      return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    default:
      return 'text-gray-500 bg-gray-800 border-gray-700';
  }
}

interface ParsedSkill {
  name: string;
  scope?: string;
  description?: string;
  enabled?: boolean;
}

function parseSkillsFromText(text: string): {
  count: number;
  skills: ParsedSkill[];
} {
  const lines = text.split('\n');
  const countMatch = text.match(/^(\d+) skill/);
  const count = countMatch ? parseInt(countMatch[1]) : 0;
  const skills: ParsedSkill[] = [];

  for (const line of lines) {
    // Format: **name** [scope] — description  OR  **name** [scope]
    const m = line.match(/\*\*(.+?)\*\*\s*(?:\[(.+?)\])?\s*(?:[—-]\s*(.+))?/);
    if (m) {
      skills.push({
        name: m[1].trim(),
        scope: m[2]?.trim(),
        description: m[3]?.trim(),
      });
    }
  }
  return { count, skills };
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function SkillListResult({ text }: { text: string }) {
  const { t } = useTranslation('pleri/chat');
  const [open, setOpen] = useState(false);
  const { count, skills } = parseSkillsFromText(text);
  const displayCount = count || skills.length;

  return (
    <div>
      <button
        className="flex w-full items-center gap-2 border-t border-gray-800 px-3 py-2 text-left transition-colors hover:bg-gray-900/40"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs text-gray-500">
          {displayCount > 0
            ? t('skillCount', { count: displayCount })
            : t('noSkills')}
        </span>
        <span className="flex-1" />
        <ChevronIcon open={open} />
      </button>
      {open && (
        <div className="divide-y divide-gray-800/50">
          {skills.length > 0 ? (
            skills.map((s) => (
              <div
                key={s.name}
                className="flex items-start gap-2.5 px-3 py-2.5"
              >
                <ScrollText className="mt-0.5 h-3 w-3 flex-shrink-0 text-[#FF732E]/50" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-medium text-gray-200">
                      {s.name}
                    </span>
                    {s.scope && (
                      <span
                        className={`rounded border px-1.5 py-px text-xs font-semibold tracking-wider uppercase ${scopeStyle(s.scope)}`}
                      >
                        {s.scope}
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <span className="truncate text-xs text-gray-600">
                      {s.description}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 font-mono text-xs whitespace-pre-wrap text-gray-600">
              {text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateResult({
  skillName,
  resultText,
}: {
  skillName: string | null;
  resultText: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const isError = /fail|permission|error/i.test(resultText);
  return (
    <div className="border-t border-gray-800 px-3 py-2">
      {isError ? (
        <span className="text-xs text-red-400">
          {resultText.split('\n')[0]}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <ScrollText className="h-3 w-3 flex-shrink-0 text-[#FF732E]/60" />
          <span className="flex-1 truncate font-mono text-xs text-gray-200">
            {skillName ?? '—'}
          </span>
          <span className="flex-shrink-0 rounded-md bg-emerald-900/30 px-1.5 py-0.5 text-xs font-medium text-emerald-400">
            {t('createdBadge')}
          </span>
        </div>
      )}
    </div>
  );
}

function UpdateResult({
  skillName,
  input,
  resultText,
}: {
  skillName: string | null;
  input: Record<string, unknown>;
  resultText: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const isError = /fail|permission|error/i.test(resultText);

  // Collect what actually changed from the input
  const changes: string[] = [];
  if (input.displayName) changes.push(`name → "${input.displayName}"`);
  if (input.description) changes.push('description');
  if (input.systemPrompt !== undefined) changes.push('prompt');
  if (input.enabled !== undefined) changes.push(`enabled → ${input.enabled}`);
  if (input.workspace) changes.push('workspace');

  return (
    <div className="border-t border-gray-800 px-3 py-2">
      {isError ? (
        <span className="text-xs text-red-400">
          {resultText.split('\n')[0]}
        </span>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <ScrollText className="h-3 w-3 flex-shrink-0 text-[#FF732E]/60" />
            <span className="flex-1 truncate font-mono text-xs text-gray-200">
              {skillName ?? '—'}
            </span>
            <span className="flex-shrink-0 rounded-md bg-gray-800 px-1.5 py-0.5 text-xs font-medium text-gray-500">
              {t('updated')}
            </span>
          </div>
          {changes.length > 0 && (
            <div className="ml-[18px] flex flex-wrap gap-1">
              {changes.map((c) => (
                <span
                  key={c}
                  className="rounded bg-gray-800/80 px-1.5 py-0.5 font-mono text-xs text-gray-500"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteResult({
  skillName,
  resultText,
}: {
  skillName: string | null;
  resultText: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const isError = /fail|permission|error|not found/i.test(resultText);
  return (
    <div className="border-t border-gray-800 px-3 py-2">
      {isError ? (
        <span className="text-xs text-red-400">
          {resultText.split('\n')[0]}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500/40" />
          <span className="flex-1 truncate font-mono text-xs text-gray-600 line-through decoration-red-500/50">
            {skillName ?? '—'}
          </span>
          <span className="flex-shrink-0 rounded-md bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-400">
            {t('deleted')}
          </span>
        </div>
      )}
    </div>
  );
}

function ForkResult({
  newSkillName,
  fromTemplate,
  resultText,
}: {
  newSkillName: string | null;
  fromTemplate: string | null;
  resultText: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const isError = /fail|permission|error|not found/i.test(resultText);
  return (
    <div className="border-t border-gray-800 px-3 py-2">
      {isError ? (
        <span className="text-xs text-red-400">
          {resultText.split('\n')[0]}
        </span>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ScrollText className="h-3 w-3 flex-shrink-0 text-[#FF732E]/60" />
            <span className="flex-1 truncate font-mono text-xs text-gray-200">
              {newSkillName ?? '—'}
            </span>
            <span className="flex-shrink-0 rounded-md bg-emerald-900/30 px-1.5 py-0.5 text-xs font-medium text-emerald-400">
              {t('forked')}
            </span>
          </div>
          {fromTemplate && (
            <span className="ml-[18px] font-mono text-xs text-gray-600">
              {t('fromTemplate', { fromTemplate })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteFileResult({
  skillName,
  filePath,
  resultText,
}: {
  skillName: string | null;
  filePath: string | null;
  resultText: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const isError = /fail|permission|error/i.test(resultText);
  const label = filePath ? `${skillName ?? '?'}/${filePath}` : skillName;
  return (
    <div className="border-t border-gray-800 px-3 py-2">
      {isError ? (
        <span className="text-xs text-red-400">
          {resultText.split('\n')[0]}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500/40" />
          <span className="flex-1 truncate font-mono text-xs text-gray-600 line-through decoration-red-500/50">
            {label ?? '—'}
          </span>
          <span className="flex-shrink-0 rounded-md bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-400">
            {t('deleted')}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TOOL_LABELS: Record<string, string> = {
  listPleriSkills: 'Skills',
  createPleriSkill: 'Create skill',
  updatePleriSkill: 'Update skill',
  deletePleriSkill: 'Delete skill',
  deletePleriSkillFile: 'Delete skill file',
  forkTemplate: 'Fork template',
};

export const SkillManagementBlock: React.FC<{ content: ToolUseBlock }> = ({
  content,
}) => {
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (
          s.conversations[activeConversationId]?.pendingToolUses ?? new Set()
        ).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);
  const isRunning = isPending || !resultJson;

  const label = TOOL_LABELS[content.name] ?? content.name;

  // Parse result text
  let resultText = '';
  if (resultJson) {
    try {
      const parsed = JSON.parse(resultJson);
      resultText = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
    } catch {
      resultText = resultJson;
    }
  }

  // Extract skill name — check all param variants (new: `name`, old: `id`, legacy: `skillId`)
  const rawSkillId = (
    content.input?.name ??
    content.input?.id ??       // old schema before rename
    content.input?.skillId ??  // legacy
    null
  ) as string | null;

  // For create: `name` is optional (auto-generated from displayName).
  // Parse "Name: xxx" from the result text when input name is absent.
  const nameFromResult = resultText.match(/^Name:\s*(.+)$/m)?.[1]?.trim() ?? null;

  const skillName =
    extractSlug(rawSkillId) ??
    (content.name === 'createPleriSkill' ? nameFromResult : null) ??
    null;

  // For fork: template source + new skill name
  const rawTemplateId = (content.input?.templateId ?? content.input?.name ?? null) as string | null;
  const templateSlug = extractSlug(rawTemplateId);
  const rawNewName = (content.input?.newDisplayName ?? null) as string | null;

  // For deleteFile: skillId + path
  const filePath = (content.input?.path ?? null) as string | null;

  // For update: gather changed fields
  const updateInput = (content.input as Record<string, unknown>) ?? {};

  // Header label: show which skill is being operated on
  const headerSuffix =
    content.name === 'forkTemplate'
      ? (templateSlug ?? skillName)   // fork: show source template name
      : content.name !== 'listPleriSkills' && skillName
        ? skillName
        : null;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#FF732E]/10">
          <ScrollText className="h-3.5 w-3.5 text-[#FF732E]" />
        </div>
        <span className="flex-1 text-xs font-medium text-gray-300">
          {label}
        </span>
        {headerSuffix && (
          <span className="max-w-[180px] truncate font-mono text-xs text-gray-600">
            {headerSuffix}
          </span>
        )}
        {isRunning ? (
          <Spinner className="text-yellow-400" />
        ) : (
          <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
        )}
      </div>

      {/* Result body */}
      {!isRunning &&
        resultText &&
        (() => {
          switch (content.name) {
            case 'listPleriSkills':
              return <SkillListResult text={resultText} />;
            case 'createPleriSkill':
              return (
                <CreateResult skillName={skillName} resultText={resultText} />
              );
            case 'updatePleriSkill':
              return (
                <UpdateResult
                  skillName={skillName}
                  input={updateInput}
                  resultText={resultText}
                />
              );
            case 'deletePleriSkill':
              return (
                <DeleteResult skillName={skillName} resultText={resultText} />
              );
            case 'deletePleriSkillFile':
              return (
                <DeleteFileResult
                  skillName={skillName}
                  filePath={filePath}
                  resultText={resultText}
                />
              );
            case 'forkTemplate':
              return (
                <ForkResult
                  newSkillName={rawNewName}
                  fromTemplate={templateSlug}
                  resultText={resultText}
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
};
